<template>
    <div class="report-list" ref="reportList">
        <ReportListFilters
            :filter="filters"
            :sort="sort"
            @filter="filterReports"
            @sort="sortReports"
            v-sticky
        />
        <div v-for="report in filteredAndSortedReports" :key="report.id">
            <ReportListItem :report="report" v-if="['CASE_ACTIVE', 'CASE_ARCHIVED'].includes(report.status) "/>
            <ReportListItemEdit :report="report" v-if="report.status === 'CASE_EDIT'" @update-report="data => updateReport(report.id, data)" />
        </div>

        <div v-if="!filteredAndSortedReports.length" class="report-list__no-results">
            <h3>Brak wyników dla wybranych filtrów</h3>
            <button class="btn btn-blue" @click="resetFilters">Resetuj filtry</button>
        </div>
    </div>
</template>

<script>
import ReportListFilters from "@/components/reports/list/ReportListFilters";
import ReportListItem from "@/components/reports/list/ReportListItem";
import ReportListItemEdit from "@/components/reports/list/ReportListItemEdit";
import { mapGetters } from 'vuex';

export default {
    name: "ReportList",
    props: {
        reports: {
            required: true,
            type: Array,
        },
    },
    components: {
        ReportListFilters,
        ReportListItem,
        ReportListItemEdit,
    },
    data() {
        return {
            filters: {
                patientGender: "",
                caseNumber: "",
                caseCreatedAt: "",
                caseisReporter: "",
                caseSurgicalEvaluationCode: "",
                caseExpectedSurgeryDate: "",
                caseChemotherapyEffectiveness: "",
                caseLastChemotherapyCycleAt: "",
                caseProcedureRiskCode: "",
                doctorPwz: "",
                folderId: "",
            },
            sort: {
                field: "",
                order: "desc",
            },
            mappedFiltersToPaths: {
                id: "id",
                patientGender: "patient.gender",
                patientAge: "patient.age",
                doctorPwz: "doctor.pwz",
                caseNumber: "case.number",
                caseCreatedAt: "case.createdAt",
                caseisReporter: "case.isReporter",
                caseSurgicalEvaluationCode: "case.surgicalEvaluation.code",
                caseSurgicalEvaluationNote: "case.surgicalEvaluation.note",
                caseExpectedSurgeryDate: "case.expectedSurgeryDate",
                caseChemotherapyEffectiveness: "case.chemotherapyEffectiveness",
                caseLastChemotherapyCycleAt: "case.lastChemotherapyCycleAt",
                caseProcedureRiskCode: "case.procedureRisk.code",
                caseProcedureRiskNote: "case.procedureRisk.note",
                folderId: "case.folderId",
            },
            latestOpenedId: "",
        };
    },
    computed: {
        filteredAndSortedReports: function () {
            // get object value by path (e.g. report.case.number)
            const getValueByPath = (obj, path) => path.split(".").reduce((o, i) => o[i], obj);

            // find report by filters
            const filteredReports = this.reports.filter((report) => {
                return Object.keys(this.filters).every((key) => {
                    const value = this.filters[key];
                    const path = this.mappedFiltersToPaths[key];
                    const reportValue = getValueByPath(report, path) ?? "null";

                    if (value === "") return true;

                    return reportValue.toString().toLowerCase().includes(value.toLowerCase());
                });
            });

            // sort reports by field using field path
            const sortedReports = filteredReports.sort((a, b) => {
                const aValue = getValueByPath(a, this.sort.field);
                const bValue = getValueByPath(b, this.sort.field);

                if (aValue < bValue) {
                    return this.sort.order === "asc" ? -1 : 1;
                } else if (aValue > bValue) {
                    return this.sort.order === "asc" ? 1 : -1;
                } else {
                    return 0;
                }
            });

            // move reports with status "CASE_ARCHIVED" to the end of the list
            const sortedReportsWithArchived = [
                ...sortedReports.filter((report) => report.status !== "CASE_ARCHIVED"),
                ...sortedReports.filter((report) => report.status === "CASE_ARCHIVED"),
            ];

            return sortedReportsWithArchived;
        },
        ...mapGetters({
            reportTable: 'getReportTable'
        })
    },
    methods: {
        resetFilters() {
            this.filters = {
                patientGender: "",
                caseNumber: "",
                caseCreatedAt: "",
                caseisReporter: "",
                caseSurgicalEvaluationCode: "",
                caseExpectedSurgeryDate: "",
                caseChemotherapyEffectiveness: "",
                caseLastChemotherapyCycleAt: "",
                caseProcedureRiskCode: "",
                doctorPwz: "",
                folderId: "",
            };
        },
        sortReports(field) {
            if (this.sort.field === field) {
                this.sort.order = this.sort.order === "desc" ? "asc" : this.sort.order === "asc" ? "" : "desc";
                if (this.sort.order === "") {
                    this.sort.field = "";
                }
            } else {
                this.sort.field = field;
                this.sort.order = "desc";
            }

            this.addParamsToUrl();
        },
        filterReports(filters) {
            this.filters = filters;
            this.addParamsToUrl();
        },
        updateReport(reportId, updatedData) {
            // find and update report
            let report = this.reports.find((report) => report.id === reportId);

            Object.assign(report, {
                case: {
                    ...report.case,
                    expectedSurgeryDate: updatedData.expectedSurgeryDate,
                    chemotherapyEffectiveness: updatedData.chemotherapyEffectiveness,
                    lastChemotherapyCycleAt: updatedData.lastChemotherapyCycleAt,
                    procedureRisk: {...updatedData.procedureRisk},
                    surgicalEvaluation: {...updatedData.surgicalEvaluation},
                },
            });
        },
        scrollToLatestOpened() {
            if (!this.latestOpenedId) return;
            this.$nextTick(() => {
                const latestOpened = this.$refs.reportList.querySelector(`[data-id="${this.latestOpenedId}"]`);
                if (!latestOpened) return;
                latestOpened.scrollIntoView({ behavior: "smooth" });
                latestOpened.classList.add("report__item--highlight");
                this.setLatestOpened('')
                this.$store.state.reportTable.latestOpenedReportId = ""
            })
        },
        setLatestOpened(id) {
            this.latestOpenedId = id;
        },
        addParamsToUrl() {

            const filters = Object.entries(this.filters).reduce((acc, [key, value]) => {
                if (!value) return acc
                return `${acc ? acc + "&" : ''}${key}:${value}`
            }, '');

            console.log(filters)

            const params = new URLSearchParams();

            if (this.sort.field && this.sort.order) {
                params.append('sort', `${this.sort.field}:${this.sort.order}`);
            }

            if (Object.keys(filters).length) {
                params.append('filter', filters);
            }

            history.pushState({}, "", `${location.pathname}?${params}`);
        },

        setFiltersFromUrl() {
            const filters = new URLSearchParams(location.search).get('filter');

            if (filters) {
                const newFilters = filters.split('&').reduce((acc, filter) => {
                    const [key, value] = filter.split(':');
                    acc[key] = value;
                    return acc;
                }, {});

                this.filters = {
                    ...this.filters,
                    ...newFilters
                };
            }

            const sort = new URLSearchParams(location.search).get('sort')

            if (sort) {
                const [field, order] = sort.split(':');
                this.sort = {
                    field,
                    order
                };
            }
        },
    },
    beforeMount() {
        this.latestOpenedId = this.reportTable.latestOpenedReportId;
        this.setFiltersFromUrl();
    },
    mounted() {
        this.scrollToLatestOpened();
    },
};
</script>

<style lang="scss">
.list {
    width: 100%;
    height: 100%;
    background: #fff;
    z-index: 999;
    flex: 1;
}

.report-list__no-results {
    padding: 4rem 2rem;
    text-align: center;
    background: linear-gradient(to bottom, #fafafa, #fff);
}

.report-list__field {
    width: auto !important;
    width: calc(100% - 30px) !important;
    max-width: calc(100% - 30px) !important;
    flex: 1;
    border-radius: 5px !important;
    border: 1px solid #e1e1e1 !important;
    font-size: 14px;
}

.report__item--highlight {
    animation: hightlight 3s;
    animation-delay: 1s;
}

@keyframes hightlight {
    0% {
        background: #fff;
    } 20% {
        background: rgb(255, 243, 221);
    } 100% {
        background: #fff;
    }
}
</style>
